<template>
  <StackPageLayout @close="navigateToMoreMenu">
    <template #header>
      <div class="flex w-full items-center justify-between">
        <PageTitle> Raw Transactions </PageTitle>
        <BaseDatePickerDrawer v-model="dateFilter" />
      </div>
    </template>
    <QueryLoading v-if="isLoading" />
    <QueryError v-else-if="isError && data === undefined">
      Failed to retrieve transactions
    </QueryError>
    <template v-else-if="data">
      <div class="mb-3">
        <template
          v-for="(
            [date, transactions], index
          ) in transactionsGroupedByDate"
          :key="date"
        >
          <div
            :class="[
              'cursor-pointer px-2 pb-3 text-sm',
              index === 0 ? '' : 'pt-3',
            ]"
          >
            {{ date }}
          </div>
          <ol
            class="-mx-4 mb-6 space-y-px bg-background-fg/10 py-px"
          >
            <template
              v-for="transaction in transactions"
              :key="transaction.id"
            >
              <li>
                <TransactionListItem
                  :transaction="transaction"
                  @click="onClickTransaction(transaction)"
                />
              </li>
            </template>
          </ol>
        </template>
      </div>
      <div class="flex justify-around px-6">
        <BasePagination
          v-model:page="page"
          :page-count="pageCount"
        />
      </div>
    </template>
  </StackPageLayout>

  <TransactionDrawer
    v-if="currentTransaction"
    :is-open="isTransactionDrawerOpen"
    :transaction="currentTransaction"
    @close="handleCloseTransactionDrawer"
  />
</template>

<script setup lang="ts">
import {
  type ListTransactionsFilter,
  type ListTransactionsResponseItem,
  useTransactions,
} from "@/services/transactions/listTransactions"

const router = useRouter()

const dateFilter = ref<string | null>(null)

const filters = computed<ListTransactionsFilter>(() => ({
  date: dateFilter.value,
}))

const {
  paginationQuery,
  page,
  pageCount,
  resetPagination,
} = usePagination({
  limit: 10,
  count: computed(() => data.value?.count || 0),
})

watch(
  () => filters.value,
  () => resetPagination(),
  { deep: true, immediate: true }
)

const query = reactiveComputed(() => ({
  ...filters.value,
  ...paginationQuery.value,
}))

const { data, isError, isLoading } = useTransactions(query)

const transactionsGroupedByDate = computed(() => {
  if (!data.value) return
  return data.value.items.reduce(
    (accumulator, transaction) => {
      const datePart = formatToDate(transaction.createdAt)
      return accumulator.set(datePart, [
        ...(accumulator.get(datePart) || []),
        transaction,
      ])
    },
    new Map<string, ListTransactionsResponseItem[]>()
  )
})

const navigateToMoreMenu = () => {
  router.push({ name: "moreMenu" })
}

const currentTransaction =
  ref<ListTransactionsResponseItem>()
const isTransactionDrawerOpen = ref(false)
const showTransactionDrawer = () => {
  isTransactionDrawerOpen.value = true
}
const handleCloseTransactionDrawer = () => {
  isTransactionDrawerOpen.value = false
}

const onClickTransaction = (
  transaction: ListTransactionsResponseItem
) => {
  if (
    transaction.status === "cancelled" ||
    transaction.status === "pending"
  )
    return

  currentTransaction.value = transaction
  showTransactionDrawer()
}
</script>
